import { useEffect, useState } from 'react';
import { CognitoService } from '@/auth/CognitoService';
import { useDeploymentStore } from '@/store/deploymentStore';
import { Screen } from '../ui';
import NotFound from './NotFound';

/**
 * Admin-only. A non-admin sees the same not-found page as any unknown route rather than
 * an error that confirms the route exists.
 */
export default function AdminMetrics() {
  const [metrics, setMetrics] = useState<Record<string, unknown> | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      const base = (useDeploymentStore.getState().config?.apiBaseUrl ?? '').replace(/\/+$/, '');
      const token = CognitoService.getAccessTokenSync();
      if (!base || !token) {
        if (live) setForbidden(true);
        return;
      }
      try {
        const res = await fetch(`${base}/metrics`, { headers: { Authorization: `Bearer ${token}` } });
        if (!live) return;
        if (res.status === 401 || res.status === 403) {
          setForbidden(true);
          return;
        }
        if (!res.ok) {
          setError(`The server returned ${res.status}.`);
          return;
        }
        setMetrics((await res.json()) as Record<string, unknown>);
      } catch {
        if (live) setError('Could not reach the server.');
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  if (forbidden) return <NotFound />;

  return (
    <Screen title="Metrics">
      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
      {metrics && <pre className="submission-row type-caption">{JSON.stringify(metrics, null, 2)}</pre>}
    </Screen>
  );
}
